import { getAxios, getAxiosWithoutLoading } from '../axios';
import {
  ENDPOINT_CLIENTS,
  ENDPOINT_CLIENTS_FORGOT
} from '../../config/endpoints';
import {
  getTokenHeaders,
  addParam,
  errorCatch, getNotificationId,
} from '../helper';
import { Messages } from '../../config';
import { store } from '../../store/store';
import { NOTIFICATION_SUCCESS } from '../../store/actions/types';
import { displayNotification } from '../../store/actions/notification';

/**
 * Get list of client users
 * @param {object} payload
 * @param {boolean} [payload.disablePagination]
 * @param {boolean} [payload.hideNotification]
 * @param {object} payload.pagination
 * @param {number} payload.pagination.page
 * @param {number} payload.pagination.per_page
 * @param {string} payload.pagination.sort_by
 * @param {string} payload.pagination.sort
 * @param {string} payload.pagination.q
 * @returns {Promise<T>}
 */
export function getUsers(payload) {
  let axiosFunction = getAxios;

  if (payload.hideLoading) {
    axiosFunction = getAxiosWithoutLoading;
  }

  let headersAndParams = getTokenHeaders( payload.pagination ? payload.pagination : payload.disablePagination ? {} : { per_page: 15 } );

  return axiosFunction( payload.hideNotification )
    .get(
      addParam( ENDPOINT_CLIENTS, {
        "{user}": "",
      } ),
      {
        headers: headersAndParams.headers,
        params: {
          ...headersAndParams.params,
          disablePagination: payload.disablePagination
        },
      }
    )
    .then( (response) => {
      return response && response.data ? response.data : null;
    } )
    .catch( (e) => {
      errorCatch( e, {
        401: Messages.UNAUTHENTICATED_ERROR,
        403: Messages.UNAUTHORIZED_ERROR,
        404: Messages.NOT_FOUND_ERROR,
      } );

      return e.response;
    } );
}

/**
 * Get details of client user
 * @param {object} payload
 * @param {string} payload.userId
 * @param {string} payload.notificationId
 * @param {boolean} [payload.hideNotification]
 * @returns {Promise<T>}
 */
export function getUserDetails(payload) {
  return getAxios(payload.hideNotification)
    .get(
      addParam( ENDPOINT_CLIENTS, {
        "{user}": payload.userId,
      } ),
      getTokenHeaders()
    )
    .then( (response) => {

      return response && response.data ? response.data : null;

    } )
    .catch( (e) => {
      errorCatch( e, {
        401: Messages.UNAUTHENTICATED_ERROR,
        403: Messages.UNAUTHORIZED_ERROR,
        404: Messages.NOT_FOUND_ERROR,
      }, getNotificationId( payload ) );

      return e.response;
    } );
}

/**
 * Edit an user
 * @param {object} payload
 * @param {string} payload.notificationId
 * @param {object} payload.data
 * @param {number} payload.data.id
 * @param {string} payload.data.first_name
 * @param {string} payload.data.last_name
 * @param {string} payload.data.email
 * @returns {Promise<T>}
 */
export function updateUser(payload) {
  return getAxios()
    .put(
      addParam( ENDPOINT_CLIENTS, {
        "{user}": payload.data.id.toString(),
      } ),
      payload.data,
      getTokenHeaders()
    )
    .then( (response) => {
      if (response && response.data) {
        store.dispatch(
          displayNotification(
            NOTIFICATION_SUCCESS,
            Messages.UPDATE_RESOURCE_SUCCESS,
            payload.notificationId
          )
        );

        return response.data;
      }

      return null;
    } )
    .catch( (e) => {
      errorCatch( e, {
        400: 'concat',
        401: Messages.UNAUTHENTICATED_ERROR,
        403: Messages.UNAUTHORIZED_ERROR,
        404: Messages.NOT_FOUND_ERROR,
      }, getNotificationId( payload ) );

      return e.response;
    } );
}

/**
 * Delete a client user
 * @param {object} payload
 * @param {string} payload.userId
 * @param {string} payload.notificationId
 * @returns {Promise<T>}
 */
export function deleteUser(payload) {

  return getAxios( false ).delete(
    addParam(
      ENDPOINT_CLIENTS,
      {
        '{user}': '',
      }
    ),
    {
      ...getTokenHeaders(),
      data: {
        users: [
          payload.userId,
        ]
      }
    }
  )
    .then(
      response => {
        if (response && response.status === 200) {
          store.dispatch( displayNotification( NOTIFICATION_SUCCESS, Messages.CLIENT_DELETION_INITIATED, getNotificationId( payload ) ) );
          return response;
        }

        return null;
      }
    )
    .catch(
      e => {
        errorCatch( e, {
          401: Messages.UNAUTHENTICATED_ERROR,
          403: Messages.UNAUTHORIZED_ERROR,
          404: Messages.NOT_FOUND_ERROR,
        }, getNotificationId(payload) );

        return e.response;
      }
    );
}

/**
 * Lock a client user account
 * @param {object} payload
 * @param {string} payload.userId
 * @param {string} payload.notificationId
 * @returns {Promise<T>}
 */
export function lockUser(payload) {

  return getAxios(false).put(
    addParam(
      ENDPOINT_CLIENTS,
      {
        '{user}': 'lock',
      }
    ),
    {
      users: [
        payload.userId,
      ]
    },
    getTokenHeaders(),
  )
    .then(
      response => {
        if (response && response.status === 200) {
          store.dispatch(displayNotification(NOTIFICATION_SUCCESS, Messages.USER_DEACTIVATED, getNotificationId(payload)));
          return response;
        }

        return null;
      }
    )
    .catch(
      e => {
        errorCatch(e, {
          401: Messages.UNAUTHENTICATED_ERROR,
          403: Messages.UNAUTHORIZED_ERROR,
          404: Messages.NOT_FOUND_ERROR,
        }, getNotificationId(payload));

        return e.response;
      }
    );
}

/**
 * Reset a client user password
 * @param {object} payload
 * @param {string} payload.email
 * @param {string} payload.notificationId
 * @returns {Promise<T>}
 */
 export function resetUserPassword(payload) {
  return getAxios().post(
    ENDPOINT_CLIENTS_FORGOT,
    payload,
    getTokenHeaders()
  )
    .then(response => {
      if (response && response.data && response.status === 201) {
        store.dispatch(displayNotification(NOTIFICATION_SUCCESS, Messages.INVALIDATE_USER_PASSWORD_SUCCESS, getNotificationId(payload)));

        return response;
      }

      return null
    })
    .catch(
      e => {
        errorCatch(e, {
          400: 'concat',
          401: Messages.UNAUTHENTICATED_ERROR,
          403: Messages.UNAUTHORIZED_ERROR,
          404: Messages.NOT_FOUND_ERROR,
        }, getNotificationId(payload));

        return e.response;
      }
    );
}

/**
 * Lock a client user account
 * @param {object} payload
 * @param {string} payload.userId
 * @param {string} payload.notificationId
 * @returns {Promise<T>}
 */
 export function unlockUser(payload) {

  return getAxios(false).put(
    addParam(
      ENDPOINT_CLIENTS,
      {
        '{user}': 'unlock',
      }
    ),
    {
      users: [
        payload.userId,
      ]
    },
    getTokenHeaders(),
  )
    .then(
      response => {
        if (response && response.status === 200) {
          store.dispatch(displayNotification(NOTIFICATION_SUCCESS, Messages.USER_REACTIVATED, getNotificationId(payload)));
          return response;
        }

        return null;
      }
    )
    .catch(
      e => {
        errorCatch(e, {
          401: Messages.UNAUTHENTICATED_ERROR,
          403: Messages.UNAUTHORIZED_ERROR,
          404: Messages.NOT_FOUND_ERROR,
        }, getNotificationId(payload));

        return e.response;
      }
    );
}
